import React from 'react';
import { IFormData, IFormColumn } from '@/interfaces/form.model';

interface IFormPreviewProps {
  formData: IFormData;
}

const FormPreview: React.FC<IFormPreviewProps> = ({ formData }) => {
  return (
    <div className="card shadow-sm border-0 h-100 bg-light">
      <div className="card-header bg-white border-bottom-0 pt-4 pb-3 px-4">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="card-title mb-0">Live Preview</h2>
          <span
            className={`badge text-white fw-normal py-1 px-3 ${formData.status === 'published' ? 'bg-success' : 'bg-warning text-dark'} rounded-pill`}
          >
            {formData?.status
              ? String(formData?.status)?.toUpperCase()
              : 'DRAFT'}
          </span>
        </div>
      </div>
      <div className="card-body p-0 overflow-auto">
        <div className="preview-container bg-white p-4 rounded shadow-sm border">
          {/* Form Header */}
          <div className="mb-5 border-bottom pb-3">
            <h4 className="mb-2">{formData.name || 'Untitled Form'}</h4>
            {formData.description && (
              <p className="text-muted mb-0">{formData.description}</p>
            )}
          </div>

          {/* Form Sections */}
          {formData?.sections && formData?.sections?.length > 0 ? (
            formData?.sections?.map((section, sIndex) => (
              <div key={section?.id || sIndex} className="mb-5">
                <h4 className="mb-4 text-primary">{section?.name}</h4>
                <div className="row g-4">
                  {section?.columns && section?.columns?.length > 0 ? (
                    section?.columns?.map((field) => (
                      <PreviewField key={field?.id} field={field} />
                    ))
                  ) : (
                    <div className="col-12">
                      <p className="text-muted fst-italic">
                        No fields in this section yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-muted py-5">
              <i className="bi bi-file-earmark-text fs-1 mb-3 d-block"></i>
              <p>Add sections and fields to see preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper Component for Preview Fields
const PreviewField: React.FC<{ field: IFormColumn }> = ({ field }) => {
  const id = field?.id;
  const renderInput = () => {
    switch (field?.columnDatatype) {
      case 'textarea':
        return (
          <textarea
            className="form-control"
            id={id}
            placeholder={field?.placeholder}
            required={field?.required}
            rows={3}
            disabled
          />
        );
      case 'select':
        return (
          <select
            className="form-select"
            id={id}
            required={field.required}
            disabled
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field?.options?.map((opt, i) => (
              <option key={i} value={opt?.value}>
                {opt?.label}
              </option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="d-flex flex-column gap-2 mt-2">
            {field?.options?.map((opt, i) => (
              <div className="form-check" key={i}>
                <input
                  className="form-check-input"
                  type="radio"
                  name={id}
                  id={`${id}-${i}`}
                  disabled
                />
                <label className="form-check-label" htmlFor={`${id}-${i}`}>
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        );
      case 'checkbox':
        if (field?.options && field?.options?.length > 0) {
          return (
            <div className="d-flex flex-column gap-2 mt-2">
              {field?.options?.map((opt, i) => (
                <div className="form-check" key={i}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${id}-${i}`}
                    disabled
                  />
                  <label className="form-check-label" htmlFor={`${id}-${i}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id={id}
              required={field.required}
              disabled
            />
            <label className="form-check-label" htmlFor={id}>
              {field.placeholder || field.label}
            </label>
          </div>
        );
      default:
        return (
          <input
            type={
              field.columnDatatype === 'date'
                ? 'date'
                : field.columnDatatype === 'number'
                  ? 'number'
                  : field.columnDatatype === 'email'
                    ? 'email'
                    : field.columnDatatype === 'file'
                      ? 'file'
                      : 'text'
            }
            className="form-control"
            id={id}
            placeholder={field.placeholder}
            required={field.required}
            disabled
          />
        );
    }
  };

  return (
    <div
      className={
        field.columnDatatype === 'textarea' ? 'col-12' : 'col-md-6 mb-3'
      }
    >
      <label htmlFor={id} className="form-label fw-semibold">
        {field.label} {field.required && <span className="text-danger">*</span>}
      </label>
      {renderInput()}
      {field.helpText && <div className="form-text mt-1">{field.helpText}</div>}
    </div>
  );
};

export default FormPreview;
